import React, { useState, useEffect } from 'react';
import { Row, Col, Container, Accordion } from 'react-bootstrap';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
// icons
import { MdKeyboardArrowRight } from 'react-icons/md';
import { FiLogOut } from 'react-icons/fi';
import { AiOutlineHome } from 'react-icons/ai';
import { AiOutlineFundProjectionScreen } from 'react-icons/ai';
import { FiMenu } from 'react-icons/fi';
//stores
import { useUserStore, useCompanyStore, useCartStore, useEbooksStore } from 'stores';
//api
import { logoutApi } from 'api/login';
import { getCategoryMenuNotPaginated, getMetadataCompany } from 'api/company';
//helpers
import { unSetLocal } from 'helpers';
import useLocalePath from 'utils/customHooks/useLocalePath';
import useIntranetPath from 'utils/customHooks/useIntranetPath';
import { isIntranetHomePath } from 'utils/localePath';
//constants
import { menu_backoffice } from 'constants';
//components
import CardCustom from 'components/slider/card';
import { usePermissions } from 'components/backoffice/PermissionsContext';
//styles
import 'assets/css/backoffice/adminLogo.scss';

const AsideMenuIntranet = () => {
  const { t } = useTranslation(['backoffice']);
  const { logo, name, email, loadMetaCompany, is_single_company } = useCompanyStore();
  const {
    logoutSuccess,
    setIsWorker,
    setEmail,
    setFirstName,
    setLastName,
    setBenefitedBookstore,
    setShippingAddress,
    setBillingAddress,
    isAdministrator,
    profile,
  } = useUserStore();
  const { clear, setShippingCartAddress } = useCartStore();
  const { clearEbooksData } = useEbooksStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [activeKey, setActiveKey] = useState(null);
  const [filteredMenu, setFilteredMenu] = useState([]);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const userPermissions = usePermissions();
  const localePath = useLocalePath();
  const intranetPath = useIntranetPath();

  useEffect(() => {
    const savedActiveKey = localStorage.getItem('activeAccordionKey');
    if (savedActiveKey !== null) {
      setActiveKey(savedActiveKey);
    }
  }, []);

  const handleAccordionSelect = (key) => {
    setActiveKey(key);
    localStorage.setItem('activeAccordionKey', key);
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const header = document.querySelector('.fixed-header');
    const handleScroll = () => {
      if (header) {
        if (window.scrollY > 50) {
          header.classList.add('scrolled');
        } else {
          header.classList.remove('scrolled');
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  useEffect(() => {
    const filtered = menu_backoffice.filter((item) => {
      if (!item.subMenu) {
        return false;
      }
      return item.subMenu.some((subItem) => userPermissions.includes(subItem.permission));
    });
    setFilteredMenu(filtered);
  }, [userPermissions]);

  const handleHome = async () => {
    try {
      const { data, status } = await getMetadataCompany();
      if (status === 200) {
        const matters = await getCategoryMenuNotPaginated({
          fields: 'id,name,description,genre_ibic,padre_id',
        });
        if (matters.status === 200) {
          const results = {
            ...data,
            matters: matters.data,
          };
          loadMetaCompany(results);
        } else {
          loadMetaCompany(data);
        }
      }
    } catch (e) {}
    navigate(localePath('/'));
  };

  const closeSession = async () => {
    const { status } = await logoutApi({
      refresh_token: localStorage.getItem('refresh'),
    });
    if (status === 200) {
      unSetLocal('access');
      unSetLocal('refresh');
      unSetLocal('permissions');
      unSetLocal('type_user');
      setIsWorker(false);
      setEmail('');
      setFirstName('');
      setLastName('');
      setBenefitedBookstore({});
      setBillingAddress({});
      setShippingAddress({});
      setShippingCartAddress({});
      clear();
      clearEbooksData();
      logoutSuccess();
      document.title = 'Intranet' | t('iniciarsesion');
    }
    navigate(localePath('/'));
  };

  const [imagen, setImagen] = useState(true);

  const handleImagenError = () => {
    setImagen(false);
  };

  return (
    <Container fluid className="px-0">
      <Row className="adminAsideRow container-list-menu px-0 m-0 d-flex flex-column">
        <Col lg={12} className="mt-3">
          {isMobile && (
            <Row className="fixed-header pt-3">
              <Col xs={6} className="d-flex justify-content-start">
                <div className="menu-icon" onClick={toggleMenu}>
                  <FiMenu size={30} />
                </div>
              </Col>
              <Col xs={6} className="d-flex justify-content-end">
                <NavLink
                  className="d-flex align-items-center"
                  to={intranetPath()}
                  onClick={() => handleAccordionSelect(null)}
                >
                  <AiOutlineHome size={30} color="#fff" className="me-3" />
                </NavLink>
                <div role="button" className="d-flex align-items-center" onClick={closeSession}>
                  <FiLogOut size={30} color="#fff" />
                </div>
              </Col>
            </Row>
          )}
          <CardCustom className="card-container card-container-mobile">
            <div className="card-img py-2 mt-3" onClick={handleHome} role="button">
              {imagen ? (
                <img src={logo} alt="logo" className="card-img-image" onError={handleImagenError} />
              ) : (
                <AiOutlineFundProjectionScreen size={20} color="#fff" className="me-3" />
              )}
            </div>
            <div className="card-info ms-2">
              <p>{name}</p>
              <p>{email}</p>
            </div>
          </CardCustom>
          <div>
            {!isMobile && (
              <NavLink
                className={
                  isIntranetHomePath(location.pathname)
                    ? 'm-3 mt-4 mb-0 pt-3 pb-3 ps-3 d-flex align-items-center text-decoration-none justify-content-start bg-principal'
                    : 'm-3 mt-4 mb-0 pt-3 pb-3 ps-3 d-flex align-items-center text-decoration-none justify-content-start bg-dark'
                }
                to={intranetPath()}
                onClick={() => handleAccordionSelect(null)}
              >
                <AiOutlineHome size={20} color={isIntranetHomePath(location.pathname) ? '#000' : '#fff'} className="me-3" />
                <span className={isIntranetHomePath(location.pathname) ? 'logout-intranet' : 'logout-intranet text-light'}>
                  {profile === 'library' ? t('inicio') : t('datosPrincipales')}
                </span>
              </NavLink>
            )}
            {isMobile ? (
              <div className={`mobile-menu-intranet ${menuOpen && 'open'}`}>
                <Accordion
                  className="lateral-menu menu-intranet"
                  activeKey={activeKey}
                  onSelect={handleAccordionSelect}
                >
                  {menu_backoffice.map((item, index) => {
                    if (profile === 'library') {
                      if (item.id !== 3 && item.id !== 4 && item.id !== 14) {
                        return null;
                      }
                    }
                    const filteredSubMenu = item.subMenu?.filter(
                      (subItem) => {
                        if (subItem.id === 3 && subItem.title === 'contests') {
                          if (!(isAdministrator === true && profile === 'worker')) {
                            return false;
                          }
                        }
                        if (profile === 'library') {
                          if (item.id === 3) {
                            // Para usuarios library, solo mostrar recommendBookstores en el menú webManagement
                            if (subItem.title !== 'recommendBookstores') {
                              return false;
                            }
                          }
                          if (item.id === 4 && subItem.id === 3) {
                            return false;
                          }
                          if (item.id === 4 && subItem.id === 2) {
                            return false;
                          }
                          if (item.id === 14 && subItem.id === 5) {
                            return false;
                          }
                        }
                        // Permitir recommendBookstores para usuarios library y admin incluso si is_single_company es true
                        if (is_single_company && subItem.title === 'recommendBookstores') {
                          if (profile === 'library' || (isAdministrator === true && profile === 'worker')) {
                            // Permitir para library y admin
                          } else {
                            return false;
                          }
                        }
                        if (is_single_company && subItem.title === 'librerias') {
                          return false;
                        }
                        return userPermissions.includes(subItem.permission);
                      },
                    );
                    if (!filteredSubMenu || filteredSubMenu.length === 0) return null;
                    return (
                      <Accordion.Item eventKey={index.toString()} className="lateral-menu" key={item.title}>
                        <Accordion.Header className="lateral-menu">
                          {item.icon}
                          <p className="text-light mb-0 ms-3">{t(`${item.title}`)}</p>
                        </Accordion.Header>
                        <Accordion.Body>
                          {filteredSubMenu.map((element) => (
                            <NavLink
                              key={element.id.toString()}
                              className={({ isActive }) => (isActive ? 'active' : 'element-menu')}
                              to={intranetPath(element.endpoint)}
                              onClick={() => setMenuOpen(false)}
                            >
                              {t(`${element.title}`)}
                              <MdKeyboardArrowRight size="31" />
                            </NavLink>
                          ))}
                        </Accordion.Body>
                      </Accordion.Item>
                    );
                  })}
                </Accordion>
              </div>
            ) : (
              <Accordion className="bg-dark menu-intranet" activeKey={activeKey} onSelect={handleAccordionSelect}>
                {menu_backoffice.map((item, index) => {
                  if (profile === 'library') {
                    if (item.id !== 3 && item.id !== 4 && item.id !== 14) {
                      return null;
                    }
                  }
                  const filteredSubMenu = item.subMenu?.filter(
                    (subItem) => {
                      if (subItem.id === 3 && subItem.title === 'contests') {
                        if (!(isAdministrator === true && profile === 'worker')) {
                          return false;
                        }
                      }
                      if (profile === 'library') {
                        if (item.id === 3) {
                          // Para usuarios library, solo mostrar recommendBookstores en el menú webManagement
                          if (subItem.title !== 'recommendBookstores') {
                            return false;
                          }
                        }
                        if (item.id === 4 && subItem.id === 3) {
                          return false;
                        }
                        if (item.id === 4 && subItem.id === 2) {
                          return false;
                        }
                        if (item.id === 14 && subItem.id === 5) {
                          return false;
                        }
                      }
                      // Permitir recommendBookstores para usuarios library y admin incluso si is_single_company es true
                      if (is_single_company && subItem.title === 'recommendBookstores') {
                        if (profile === 'library' || (isAdministrator === true && profile === 'worker')) {
                          // Permitir para library y admin
                        } else {
                          return false;
                        }
                      }
                      if (is_single_company && subItem.title === 'librerias') {
                        return false;
                      }
                      return userPermissions.includes(subItem.permission);
                    },
                  );
                  if (!filteredSubMenu || filteredSubMenu.length === 0) return null;
                  return (
                    <Accordion.Item eventKey={index.toString()} className="bg-dark" key={item.title}>
                      <Accordion.Header className="bg-dark">
                        {item.icon}
                        <p className="text-light mb-0 ms-3">{t(`${item.title}`)}</p>
                      </Accordion.Header>
                      <Accordion.Body>
                        {filteredSubMenu.map((element) => (
                          <NavLink
                            key={element.id.toString()}
                            className={({ isActive }) => (isActive ? 'active' : 'element-menu')}
                            to={intranetPath(element.endpoint)}
                          >
                            {t(`${element.title}`)}
                            <MdKeyboardArrowRight size="31" />
                          </NavLink>
                        ))}
                      </Accordion.Body>
                    </Accordion.Item>
                  );
                })}
              </Accordion>
            )}
            {!isMobile && (
              <div role="button" className="m-3 mt-0 ps-3 bg-dark d-flex align-items-center" onClick={closeSession}>
                <FiLogOut size={20} color="#fff" className="me-3" />
                <span className="logout-intranet text-light">{t('logout')}</span>
              </div>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default AsideMenuIntranet;
